async function parseCartAddResponse(response) {
  const contentType = response.headers.get('content-type') || '';
  const isJsonResponse = contentType.includes('application/json') || contentType.includes('text/javascript');
  if (!isJsonResponse) {
    const message = "We couldn't update your bag. Please wait a moment and try again.";
    return { status: response.status || 503, description: message, message };
  }
  return response.json();
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const services = this.closest('product-info')?.querySelector('pk-product-service-addons');
        const serviceItems = services?.getItems ? services.getItems() : [];
        if (serviceItems === null) return;
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        if (serviceItems.length) {
          const primary = { id: formData.get('id'), quantity: formData.get('quantity') || 1, properties: {} };
          if (formData.get('selling_plan')) primary.selling_plan = formData.get('selling_plan');
          for (const [key, value] of [...formData.entries()]) {
            const property = key.match(/^properties\[(.+)\]$/);
            if (property) primary.properties[property[1]] = value;
            if (property || ['id', 'quantity', 'selling_plan'].includes(key)) formData.delete(key);
          }
          [primary, ...serviceItems].forEach((item, index) => {
            formData.append(`items[${index}][id]`, item.id);
            formData.append(`items[${index}][quantity]`, item.quantity);
            if (item.selling_plan) formData.append(`items[${index}][selling_plan]`, item.selling_plan);
            for (const [name, value] of Object.entries(item.properties || {})) {
              formData.append(`items[${index}][properties][${name}]`, value);
            }
          });
        }
        config.body = formData;

        const variantId = formData.get('id') || formData.get('items[0][id]');
        const quantity = parseInt(formData.get('quantity') || formData.get('items[0][quantity]')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity, serviceItems);

        fetch(`${routes.cart_add_url.replace(/\.js$/, '')}.js`, config)
          .then(parseCartAddResponse)
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.showCartFeedback(response.description || response.message, true);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              this.rejectCartLinesUpdate(linesUpdateDeferred, new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);
            this.showCartFeedback('Added to cart');

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            this.handleErrorMessage(e.message || window.cartStrings?.error);
            this.showCartFeedback(e.message || window.cartStrings?.error || 'Please try again.', true);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            this.rejectCartLinesUpdate(linesUpdateDeferred, e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      showCartFeedback(message, failed = false) {
        let notice = document.getElementById('pk-cart-feedback');
        if (!notice) {
          const style = document.createElement('style');
          style.textContent = `
            #pk-cart-feedback{position:fixed;top:22px;left:50%;transform:translateX(-50%);z-index:2147483000;max-width:calc(100vw - 32px);padding:12px 20px;background:#10292b;color:#f3ebdd;border:1px solid #cdb782;border-radius:6px;font:14px/1.4 sans-serif;box-shadow:0 4px 18px #0005;pointer-events:none;}
            #pk-cart-feedback[hidden]{display:none;}
            .pk-corner-cart .quick-add__submit.pk-added .pk-corner-cart__icon{visibility:hidden;}
            .pk-corner-cart .quick-add__submit.pk-added:after{content:'✓';display:block!important;position:absolute;inset:0;border:0;box-shadow:none;font-size:25px;line-height:44px;color:#e6d5ad;}
          `;
          document.head.appendChild(style);
          notice = document.createElement('div');
          notice.id = 'pk-cart-feedback';
          notice.setAttribute('role', 'status');
          notice.setAttribute('aria-live', 'polite');
          document.body.appendChild(notice);
        }
        clearTimeout(notice.pkTimeout);
        notice.hidden = false;
        notice.textContent = failed ? message : '✓ ' + message;
        notice.pkTimeout = setTimeout(() => { notice.hidden = true; }, failed ? 6000 : 4000);
        if (!failed) {
          const opener = this.closest('quick-add-modal');
          const button = opener
            ? document.querySelector(`modal-opener[data-modal="#${opener.id}"] button`)
            : this.submitButton;
          if (button) {
            clearTimeout(button.pkFeedbackTimeout);
            button.classList.add('pk-added');
            button.pkFeedbackTimeout = setTimeout(() => button.classList.remove('pk-added'), 2000);
          }
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity, serviceItems = []) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }, ...serviceItems.map(item => ({ merchandiseId: String(item.id), quantity: item.quantity }))],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      rejectCartLinesUpdate(deferred, error) {
        if (!deferred) return;
        deferred.promise?.catch(() => {});
        deferred.reject(error);
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

